import React, { useState } from "react";
import { useWallet } from "../hooks/useWallet";

const STEPS = {
  INPUT: "input",
  REVIEW: "review",
  DONE: "done",
  ERROR: "error",
};

export function ClaimAgentModal({ config, agent, onClose, onClaimed }) {
  const { account } = useWallet();

  // KatbotAgent rows use canonical_name as their KatpackAgent katbook_agent_name
  const resolvedName = agent?.canonical_name ?? agent?.katbook_agent_name ?? "";

  // If agent is pre-populated, start at REVIEW and pre-fetch the claim message
  const [step, setStep] = useState(agent ? STEPS.REVIEW : STEPS.INPUT);
  const [agentName, setAgentName] = useState(resolvedName);
  const [claimData, setClaimData] = useState(null);
  const [claimedAgent, setClaimedAgent] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Auto-fetch claim message when agent is pre-populated
  React.useEffect(() => {
    if (resolvedName) {
      fetchClaimMessage(resolvedName);
    }
  }, [resolvedName]);

  const fetchClaimMessage = async (name) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${config.apiBaseUrl}/agents/${name}/claim-message`);
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `Failed to fetch claim message (${resp.status})`);
      }
      const data = await resp.json();
      if (data.owner_wallet_address.toLowerCase() !== account.toLowerCase()) {
        throw new Error(
          `This agent was registered with wallet ${data.owner_wallet_address}. ` +
            `You are connected as ${account}.`
        );
      }
      setClaimData(data);
      setStep(STEPS.REVIEW);
    } catch (err) {
      setError(err.message);
      setStep(STEPS.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchMessage = async () => {
    if (!agentName.trim()) return;
    await fetchClaimMessage(agentName.trim());
  };

  const handleSign = async () => {
    setLoading(true);
    setError(null);
    try {
      // Sign via MetaMask — personal_sign requires hex-encoded message bytes
      const msgHex = "0x" + Array.from(new TextEncoder().encode(claimData.message))
        .map(b => b.toString(16).padStart(2, "0"))
        .join("");
      const signature = await window.ethereum.request({
        method: "personal_sign",
        params: [msgHex, account],
      });
      console.log("[claim] message hex:", msgHex);
      console.log("[claim] signature:", signature);

      // Submit claim to API
      const resp = await fetch(
        `${config.apiBaseUrl}/agents/${agentName.trim()}/claim`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            wallet_address: account,
            signature,
          }),
        }
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `Claim failed (${resp.status})`);
      }
      const result = await resp.json();
      setClaimedAgent(result);
      setStep(STEPS.DONE);
      onClaimed?.(result);
    } catch (err) {
      // User rejected MetaMask signature
      if (err.code === 4001) {
        setError("You rejected the signature request.");
      } else {
        setError(err.message);
      }
      setStep(STEPS.ERROR);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Claim an Agent</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-6">
          {/* Step 1: Enter agent name */}
          {step === STEPS.INPUT && (
            <div className="space-y-4">
              <p className="text-gray-400 text-sm">
                Enter the name of the KatPack agent you want to link to your wallet.
                The agent must have been registered with your wallet address.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Agent name
                </label>
                <input
                  type="text"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleFetchMessage()}
                  placeholder="my-agent"
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-katpack-500 font-mono text-sm"
                />
              </div>
              <button
                onClick={handleFetchMessage}
                disabled={loading || !agentName.trim()}
                className="w-full bg-katpack-600 hover:bg-katpack-700 disabled:bg-gray-600 text-white font-semibold py-2.5 rounded-lg transition-colors"
              >
                {loading ? "Looking up agent…" : "Continue"}
              </button>
            </div>
          )}

          {/* Step 2: Review & sign */}
          {step === STEPS.REVIEW && loading && (
            <p className="text-gray-400 text-sm text-center py-4">Loading claim message…</p>
          )}

          {step === STEPS.REVIEW && claimData && !loading && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-yellow-400" />
                <p className="text-white font-mono font-semibold">{agentName}</p>
                <span className="text-xs text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 px-2 py-0.5 rounded">unverified</span>
              </div>
              <p className="text-gray-400 text-sm">
                Sign the message below with your wallet to prove you own this agent.
                No gas required — this is an off-chain signature only.
              </p>

              <div className="bg-gray-900 border border-gray-600 rounded-lg p-4">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">
                  Message to sign
                </p>
                <pre className="text-sm text-gray-200 whitespace-pre-wrap font-mono leading-relaxed">
                  {claimData.message}
                </pre>
              </div>

              <div className="text-xs text-gray-500 flex items-start gap-2">
                <span>ℹ</span>
                <span>
                  This is an off-chain personal sign — no gas required. MetaMask will show
                  a human-readable prompt before you sign.
                </span>
              </div>

              <button
                onClick={handleSign}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-800 text-white font-semibold py-2.5 rounded-lg transition-colors"
              >
                {loading ? (
                  "Waiting for MetaMask…"
                ) : (
                  <>
                    <span>🦊</span>
                    <span>Sign with MetaMask</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 3: Success */}
          {step === STEPS.DONE && claimedAgent && (
            <div className="space-y-4 text-center">
              <div className="text-4xl">✓</div>
              <p className="text-white font-semibold">Agent claimed!</p>
              <p className="text-gray-400 text-sm">
                <span className="font-mono text-gray-200">{claimedAgent.katbook_agent_name ?? claimedAgent.agent_name}</span> is
                now linked to your wallet.
              </p>
              <button
                onClick={onClose}
                className="w-full bg-katpack-600 hover:bg-katpack-700 text-white font-semibold py-2.5 rounded-lg transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          )}

          {/* Error state */}
          {step === STEPS.ERROR && (
            <div className="space-y-4">
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {error}
              </div>
              <button
                onClick={() => {
                  setStep(STEPS.INPUT);
                  setError(null);
                  setClaimData(null);
                }}
                className="w-full border border-gray-600 hover:border-gray-400 text-gray-300 font-semibold py-2.5 rounded-lg transition-colors"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
